import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-[#1A3B5C] via-[#2B5278] to-[#4A7BA7] overflow-hidden">
      {/* Navigation - 白背景固定 */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="/assets/senteo-corp/img/service/SENTEO.png" 
              alt="ロゴ" 
              className="h-10 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#top" className="text-sm font-medium text-[#1A3B5C] hover:text-[#4A7BA7] transition-colors cursor-pointer">
              トップ
            </a>
            <a href="#services" className="text-sm font-medium text-[#1A3B5C] hover:text-[#4A7BA7] transition-colors cursor-pointer">
              サービス内容
            </a>
            <a href="#benefits" className="text-sm font-medium text-[#1A3B5C] hover:text-[#4A7BA7] transition-colors cursor-pointer">
              導入メリット
            </a>
            <a href="#faq" className="text-sm font-medium text-[#1A3B5C] hover:text-[#4A7BA7] transition-colors cursor-pointer">
              よくある質問
            </a>
            <a 
              href="https://forms.office.com/Pages/ResponsePage.aspx?id=YKcf0P9g2U-Our9eD2Xtbv5ca4bq0tFEuCs_nTfkrFhUN0Y5NDJKVjZYM0tWSU5CMVU3TkZWTEM3VS4u"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 bg-[#4A7BA7] text-white text-sm font-medium rounded-lg hover:bg-[#3A6B97] transition-colors whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </nav>

      {/* 柔らかい光のエフェクト（ぼかし効果） */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#6BA3D6]/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#8BC4EA]/15 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>

      {/* 幾何学模様（円と波線） */}
      <div className="absolute inset-0 overflow-hidden">
        {/* 大きな円 - スマホでは非表示 */}
        <div className="hidden md:block absolute -right-20 top-1/4 w-[500px] h-[500px] rounded-full border border-white/10"></div>
        <div className="hidden md:block absolute -right-10 top-1/4 w-[400px] h-[400px] rounded-full border border-white/8"></div>
        <div className="hidden md:block absolute right-10 top-1/3 w-[300px] h-[300px] rounded-full border border-white/6"></div>

        {/* 柔らかい曲線パターン（SVG風の波線） */}
        <svg className="absolute bottom-0 left-0 w-full h-48 opacity-10" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="white" d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,154.7C672,149,768,171,864,186.7C960,203,1056,213,1152,197.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
        <svg className="absolute top-0 right-0 w-full h-32 opacity-5 rotate-180" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="white" d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,90.7C672,85,768,107,864,128C960,149,1056,171,1152,165.3C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* 微細なドットパターン */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }}></div>

      {/* 斜めのグラデーションオーバーレイ */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#1A3B5C]/30 via-transparent to-[#6BA3D6]/20"></div>

      {/* Main Content */}
      <div id="top" className="relative max-w-7xl mx-auto px-6 pt-32 pb-20 min-h-screen flex items-center">
        <div className="w-full">
          <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
            従業員のこころを守る。<br />
            それが、会社を守ること
          </h1>
          <p className="text-xl text-white/90 font-light mb-12 leading-relaxed">
            精神科専門医監修｜臨床心理士・公認心理師による<br />
            オンラインカウンセリング
          </p>
          <a 
            href="https://forms.office.com/Pages/ResponsePage.aspx?id=YKcf0P9g2U-Our9eD2Xtbv5ca4bq0tFEuCs_nTfkrFhUN0Y5NDJKVjZYM0tWSU5CMVU3TkZWTEM3VS4u"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-4 px-8 py-5 bg-[#FF6B6B] text-white text-lg font-medium rounded-full hover:bg-[#FF5252] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <span>まずはお気軽にご相談</span>
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <i className="ri-arrow-right-line text-[#FF6B6B] text-xl"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
